// DraftEditor — renders the editable draft scratchpad. The user fills
// fields and per-item photos locally; on Save the editor builds a wire
// `DraftSaveInput` and calls `createDraftRequest` (no persisted id) or
// `updateDraftRequest`, then re-baselines via `applySaveResult`. On
// Send the editor saves any dirty edits first and then chains
// `transitionRequest(id, 'send')` so the row lands in IN_WORK status —
// the server's `DRAFT_INCOMPLETE` gate surfaces missing fields when a
// non-DRAFT-ready row tries to transition. On close, the parent
// drawer runs photo cleanup against `unsavedPhotoKeys` so the close
// path never leaks orphan storage objects.
//
// @aiPurpose Compose, save, send, or discard the editable draft
//   scratchpad backed by `POST /requests/draft` and
//   `PUT /requests/:id/draft`.
// @aiInvariant `Save` is the explicit persistence affordance; `Send`
//   chains save-then-transition. Both gate locally on the same
//   strict-completeness rules the wire enforces (`name`, `route.from`,
//   `route.to`, ≥1 item, every item has a non-empty `name`,
//   `description`, positive `weight`/`volume`, integer
//   `quantity`, exactly 1 photo). After a successful save,
//   `useRequestsStore.draftPersistedId` is pinned and the post-save
//   scratchpad is snapshotted as the new baseline so the dirty flag
//   collapses back to false until the next user edit.
// @aiInvariant Photo uploads are tracked via `trackUnsavedPhoto` the
//   moment the storage PUT lands; the close path passes that batch to
//   `cleanupNeverSavedDraftPhotos` (no persisted id) or
//   `cleanupPersistedDraftPhotos(persistedId)` so a photo that never
//   reaches a saved row never lingers in storage.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { RequestStatus, isSimpleKind } from '@magimp/shared';
import { useAuthStore } from '../../../stores/auth.js';
import { useRequestsStore, isDraftDirty } from '../store.js';
import { allowedActions } from '../role.js';
import { canBeginSave, canBeginSend } from '../draft-activity.js';
import { useRequestUrlState } from '../useRequestUrlState.js';
import { invalidateForCreatedRequest, narrowInvalidateForRequest } from '../queries.js';
import {
  createDraftRequest,
  transitionRequest,
  updateDraftRequest,
} from '../../../api/requests/index.js';
import { errorMessage } from '../../../api/errors.js';
import {
  DRAFT_SAVE_ERROR_INSURANCE_TRIPLET,
  buildDraftSaveInput,
  buildSentSnapshot,
  useDraftSave,
} from '../useDraftSave.js';
import { useDraftCompleteness } from '../useDraftCompleteness.js';
import { useDraftPhotoUpload } from '../useDraftPhotoUpload.js';
import { RequestNextStepHint } from './RequestNextStepHint.js';
import { DraftBasicsCard } from './DraftBasicsCard.js';
import { DraftItemsCard } from './DraftItemsCard.js';
import { DraftStandardTotalsCard } from './DraftStandardTotalsCard.js';
import { DraftContainerTotalsCard } from './DraftContainerTotalsCard.js';
import { RequestFilesField } from './RequestFilesField.js';
import { Eyebrow } from '../../../ui/index.js';
import { Card } from './DraftEditorPrimitives.js';
import { DraftEditorFooter } from './DraftEditorFooter.js';
import type { DraftScratchpad, UserRef } from '../types.js';

export interface DraftEditorProps {
  scratchpad: DraftScratchpad;
  me: UserRef;
  /**
   * Called after a confirmed armed-delete activation. The drawer owns
   * the actual destructive flow and routes by persistence state:
   *   - persisted DRAFT or RETURN → best-effort cleanup of unsaved
   *     photo keys → `DELETE /requests/:id` → close. The backend
   *     `cleanupPersistedDraftPhotos` endpoint accepts both
   *     `DRAFT | RETURN` rows; saved-baseline photos are cleaned by
   *     the server inside `DELETE`.
   *   - never-saved scratchpad → cleanup of unsaved photo keys →
   *     `closeDraft` (no DELETE — there is no server row).
   */
  onDelete: () => Promise<void> | void;
  /** Localized error from the parent's last close attempt, or null.
   *  Rendered inline in the action footer. */
  closeError: string | null;
  /** Localized error from the parent's last DELETE attempt, or null. */
  deleteError: string | null;
  /** Store-level signal that at least one photo upload is in flight
   *  (`pendingPhotoUploads > 0`). The destructive button must read
   *  this — not just the local per-item map — so any future caller
   *  that bumps the counter still gates the delete affordance. */
  uploading: boolean;
}

export function DraftEditor({
  scratchpad: d,
  me: _me,
  onDelete,
  closeError,
  deleteError,
  uploading,
}: DraftEditorProps) {
  const qc = useQueryClient();
  const { t } = useTranslation(['requests']);
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const urlState = useRequestUrlState();
  const closeDraft = useRequestsStore((s) => s.closeDraft);
  const persistedId = useRequestsStore((s) => s.draftPersistedId);
  const baselineDraft = useRequestsStore((s) => s.baselineDraft);
  // WHY (#1594): all four lifecycle booleans now derive from the single
  //   serialized `draftActivity` phase. Save/Send begin through the
  //   machine; the footer still receives distinct booleans for its
  //   status copy + disabled states.
  const activity = useRequestsStore((s) => s.draftActivity);
  const beginActivity = useRequestsStore((s) => s.beginDraftActivity);
  const settleActivity = useRequestsStore((s) => s.settleDraftActivity);
  const saving = activity === 'saving';
  const sending = activity === 'sending';
  const closing = activity === 'closing';
  const deleting = activity === 'deleting';
  const applySaveResult = useRequestsStore((s) => s.applySaveResult);
  const saveDraft = useDraftSave();
  const [sendError, setSendError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // WHY (#1448): boxes and per-box-item photos are no longer editable by
  //   any draft kind (SIMPLE/CONTAINER already dropped them; STANDARD drops
  //   them here), so the editor only wires the per-item photo handlers.
  const { itemUploading, itemUploadError, onUploadPhoto, onAddItemPhoto } = useDraftPhotoUpload();

  const dirty = isDraftDirty(d, baselineDraft);
  const requiredErrors = useDraftCompleteness(d);

  const localUploading = Object.values(itemUploading).some(Boolean);
  const anyUploading = localUploading;

  async function onSave(): Promise<void> {
    // WHY (#1594): `saveDraft` claims/settles the machine itself; this
    //   UI gate just avoids a redundant call while busy. A double-click
    //   still issues exactly one save because the begin inside
    //   `saveDraftThroughStore` rejects the second invocation.
    if (!canBeginSave(activity)) return;
    setSaveError(null);
    const outcome = await saveDraft();
    if (!outcome.ok && outcome.error) {
      // WHY (#1061): map the insurance-triplet sentinel to the same
      //   localised key the Send gate uses so the error surface is
      //   consistent across Save and Send affordances.
      setSaveError(
        outcome.error === DRAFT_SAVE_ERROR_INSURANCE_TRIPLET
          ? t('requests:draft.required.insuranceTriplet')
          : outcome.error,
      );
    }
  }

  async function onSend(): Promise<void> {
    if (requiredErrors.length > 0 || anyUploading || !canBeginSend(activity)) return;
    // WHY (#1594): claim the machine for the whole save-then-transition
    //   sequence. A rejected begin (a second Send click before the first
    //   settles) issues NO mutation, so a double-click fires exactly one
    //   save + one transition.
    if (!beginActivity('begin-send')) return;
    setSendError(null);
    try {
      let id = persistedId;
      let createdDuringSend = false;
      // Save-or-resave first so the row exists and reflects the
      // user's latest edits before we transition it. A clean
      // already-persisted scratchpad skips the save round-trip.
      if (id === null || dirty) {
        // WHY (#1471): build the post-save baseline through `buildSentSnapshot`
        //   so its per-kind insurance decision stays in lockstep with
        //   `buildDraftSaveInput` — SIMPLE/CONTAINER force the triplet off
        //   (their save sends a forced-off triplet), while STANDARD mirrors
        //   the scratchpad (its save OMITS the triplet, preserving hidden
        //   legacy insurance, so a forced-off snapshot would spuriously
        //   re-dirty an insured legacy STANDARD draft).
        const sentSnapshot: DraftScratchpad = buildSentSnapshot(d);
        const result =
          id === null
            ? await createDraftRequest(buildDraftSaveInput(sentSnapshot, true))
            : await updateDraftRequest(id, buildDraftSaveInput(sentSnapshot, false));
        createdDuringSend = id === null;
        applySaveResult({
          persistedId: result.request.id,
          itemIdMap: result.itemIdMap,
          ...(result.boxIdMap !== undefined ? { boxIdMap: result.boxIdMap } : {}),
          ...(result.boxItemIdMap !== undefined ? { boxItemIdMap: result.boxItemIdMap } : {}),
          sentSnapshot,
        });
        id = result.request.id;
      }
      await transitionRequest(id, 'send');
      if (createdDuringSend) {
        invalidateForCreatedRequest(qc, id);
      } else {
        narrowInvalidateForRequest(qc, id);
      }
      const finalId = id;
      closeDraft();
      urlState.openRequest(finalId, 'carriers');
    } catch (err) {
      setSendError(errorMessage(err));
    } finally {
      // INVARIANT (#1594): settle on success AND failure so a failed Send
      //   never strands the machine in `'sending'` (stuck spinner). On
      //   the happy path `closeDraft()` already reset it to `'idle'`;
      //   settling again is a harmless idempotent no-op.
      settleActivity();
    }
  }

  // WHY (#1393): the TransportType picker moved into DraftBasicsCard as a
  //   CONTAINER basics field; the editor no longer renders it at the top.
  const isContainer = d.kind === 'CONTAINER';
  const isSimple = isSimpleKind(d.kind);
  // WHY (#1448): the redesigned STANDARD form mirrors CONTAINER's "drop the
  //   positions editor, mount the files block" shape but keeps the
  //   destinations + inline roads/packaging basics. It additionally renders
  //   a two-field weight/volume block and hides insurance, positions, the
  //   separate roads/packaging card, and boxes. STANDARD is the only
  //   non-SIMPLE non-CONTAINER kind.
  const isStandard = !isSimple && !isContainer;
  // WHY (#1448 + #2102): the request-level files block (#1445) is shared
  //   by SIMPLE, the redesigned CONTAINER (#1446), and STANDARD forms.
  const showFilesBlock = isSimple || isContainer || isStandard;

  // #1595: the hint's Send button reuses the EXACT readiness + lifecycle
  //   gate `onSend` enforces (no missing fields, no upload racing, machine
  //   idle). It calls the same `onSend` handler — never a duplicate send
  //   path — and only offers Send when the draft is ready.
  const canSendNow =
    requiredErrors.length === 0 && !anyUploading && !uploading && canBeginSend(activity);
  // #1595 (AC-2 FR-2): readiness is necessary but NOT sufficient — the hint
  //   may offer Send ONLY when the actor actually holds send AUTHORITY, the
  //   same `allowedActions` RBAC the drawer + backend enforce. A draft owner
  //   whose role matches `managerId` but lacks `requests.transition.send`
  //   (a stale/custom role — see role.ts canAuthor) is `send`-rejected by
  //   the backend, so the hint must not surface the CTA. `d.manager.id` is
  //   the draft's owning manager (a self-owned new DRAFT carries the current
  //   user there); admin/`ALL` and a normal owning manager with the send
  //   permission keep `sendAllowed === true`, so Send still shows for them.
  const sendParty = { actorUserId: user?.id ?? null, managerId: d.manager.id, logistId: null };
  const sendAllowed = allowedActions(d.status, permissions, sendParty).some(
    (a) => a.key === 'send' && !a.disabled,
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {(d.status === RequestStatus.DRAFT || d.status === RequestStatus.RETURN) && (
        <RequestNextStepHint
          surface="draft"
          status={d.status}
          missingCount={requiredErrors.length}
          canSend={canSendNow && sendAllowed}
          onSend={() => {
            void onSend();
          }}
        />
      )}
      <DraftBasicsCard d={d} />

      {/* WHY (#1448, FR-2): STANDARD-only weight/volume block. Rendered
       *  between basics and the files block so the request-level totals sit
       *  next to the rest of the metadata.
       *  WHY (#1448, FR-4): insurance is gone from every kind now (SIMPLE +
       *  CONTAINER already hid it; STANDARD drops it here). Hidden legacy
       *  insurance is preserved by omission on save — `DraftInsuranceCard`
       *  stays in the repo, just unreachable from the form. */}
      {isStandard && <DraftStandardTotalsCard d={d} />}

      {/* WHY (#2116, FR-A5): CONTAINER request-level weight/volume block.
       *  Mirrors the STANDARD totals card placement (between basics and the
       *  files block) — the redesigned CONTAINER form now captures these two
       *  aggregate numbers directly and the backend send-gate requires both
       *  > 0. */}
      {isContainer && <DraftContainerTotalsCard d={d} />}

      {/* WHY (#1446): the redesigned CONTAINER form removes the Positions
       *  (items) section — it renders the request-level files block in its
       *  place instead.
       *  WHY (#1448, FR-5): STANDARD also drops the Positions section (its
       *  totals live on the weight/volume block above; hidden legacy
       *  positions are preserved by omission on save). Only SIMPLE keeps the
       *  positions editor. */}
      {isSimple && (
        <DraftItemsCard
          d={d}
          persistedId={persistedId}
          itemUploading={itemUploading}
          itemUploadError={itemUploadError}
          onUploadPhoto={onUploadPhoto}
          onAddItemPhoto={onAddItemPhoto}
        />
      )}

      {/* WHY (#1446 FR-3/FR-9 + #1448 FR-3/FR-10 + #2102): request-level
       *  files/photos block for SIMPLE, CONTAINER, and STANDARD. Per #1445
       *  FR-8 the field is inert until a persisted request id exists, so we
       *  pass `persistedId` (null before the first save). */}
      {showFilesBlock && (
        <Card>
          <Eyebrow>{t('requests:draft.sections.files')}</Eyebrow>
          <div style={{ height: 12 }} />
          <RequestFilesField requestId={persistedId} />
        </Card>
      )}

      {/* WHY (#1448, FR-1/FR-6): the standalone Roads/Packaging card and the
       *  Boxes section render for NO kind anymore. SIMPLE/CONTAINER already
       *  dropped them; STANDARD now inlines roads/packaging in
       *  `DraftBasicsCard` and drops boxes (hidden legacy boxes preserved by
       *  omission on save). `DraftDictionarySelectionsCard` and
       *  `DraftBoxesSection` stay in the repo, just unreachable from the
       *  form. The box-photo upload wiring is dropped with the section. */}

      <DraftEditorFooter
        d={d}
        dirty={dirty}
        saving={saving}
        sending={sending}
        closing={closing}
        deleting={deleting}
        uploading={uploading}
        localUploading={localUploading}
        anyUploading={anyUploading}
        persistedId={persistedId}
        requiredErrors={requiredErrors}
        saveError={saveError}
        sendError={sendError}
        closeError={closeError}
        deleteError={deleteError}
        onSave={() => {
          void onSave();
        }}
        onSend={() => {
          void onSend();
        }}
        onDelete={onDelete}
      />
    </div>
  );
}
